import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from 'react';
import styles from './Avatar.module.css';

/**
 * Avatar — a shadcn-style avatar family, reimplemented in Athena's idiom
 * (TSX + CSS Module + 2.0 tokens). No Radix dependency: the image-load fallback
 * is reimplemented with a small load-status state machine shared via context —
 * AvatarImage reports load/error, AvatarFallback shows only until the image
 * loads. Tokens cascade light/dark, so no [data-theme] overrides.
 *
 * Composition:
 *   Avatar
 *   ├── AvatarImage
 *   ├── AvatarFallback
 *   └── AvatarBadge
 *
 *   AvatarGroup
 *   ├── Avatar…
 *   └── AvatarGroupCount
 */

type ImageLoadStatus = 'idle' | 'loading' | 'loaded' | 'error';
export type AvatarSize = 'sm' | 'default' | 'lg';

interface AvatarContextValue {
  status: ImageLoadStatus;
  setStatus: (s: ImageLoadStatus) => void;
  size: AvatarSize;
}

const AvatarContext = createContext<AvatarContextValue | null>(null);

const useAvatar = (): AvatarContextValue => {
  const ctx = useContext(AvatarContext);
  if (!ctx) {
    throw new Error('Avatar subcomponents must be used within <Avatar>.');
  }
  return ctx;
};

const SIZE_CLASS: Record<AvatarSize, string> = {
  sm: styles.sizeSm,
  default: styles.sizeDefault,
  lg: styles.sizeLg,
};

/* ── Avatar (root) ───────────────────────────────────────────────────── */

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: AvatarSize;
}

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ size = 'default', className = '', children, ...props }, ref) => {
    const [status, setStatus] = useState<ImageLoadStatus>('idle');
    return (
      <AvatarContext.Provider value={{ status, setStatus, size }}>
        <span
          ref={ref}
          data-slot="avatar"
          className={`${styles.avatar} ${SIZE_CLASS[size]} ${className}`.trim()}
          {...props}
        >
          {children}
        </span>
      </AvatarContext.Provider>
    );
  },
);

Avatar.displayName = 'Avatar';

/* ── AvatarImage ─────────────────────────────────────────────────────── */

export interface AvatarImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
}

const AvatarImage = ({
  src,
  alt = '',
  className = '',
  onLoad,
  onError,
  ...props
}: AvatarImageProps) => {
  const { status, setStatus } = useAvatar();

  useEffect(() => {
    setStatus(src ? 'loading' : 'error');
  }, [src, setStatus]);

  // Unmount on error so the fallback wins; keep it mounted (hidden) while
  // loading so onLoad can still fire.
  if (status === 'error' || !src) return null;

  return (
    <img
      src={src}
      alt={alt}
      data-slot="avatar-image"
      className={`${styles.image} ${status === 'loaded' ? '' : styles.imageHidden} ${className}`.trim()}
      onLoad={(e) => {
        setStatus('loaded');
        onLoad?.(e);
      }}
      onError={(e) => {
        setStatus('error');
        onError?.(e);
      }}
      {...props}
    />
  );
};

/* ── AvatarFallback ──────────────────────────────────────────────────── */

export interface AvatarFallbackProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** Delay (ms) before showing, so a fast image load doesn't flash it. */
  delayMs?: number;
}

const AvatarFallback = ({
  delayMs = 0,
  className = '',
  children,
  ...props
}: AvatarFallbackProps) => {
  const { status } = useAvatar();
  const [canShow, setCanShow] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) return undefined;
    const t = window.setTimeout(() => setCanShow(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  if (status === 'loaded' || !canShow) return null;

  return (
    <span
      data-slot="avatar-fallback"
      className={`${styles.fallback} ${className}`.trim()}
      {...props}
    >
      {children}
    </span>
  );
};

/* ── AvatarBadge ─────────────────────────────────────────────────────── */

export interface AvatarBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  position?: 'bottom-right' | 'top-right';
}

const AvatarBadge = ({
  position = 'bottom-right',
  className = '',
  children,
  ...props
}: AvatarBadgeProps) => (
  <span
    data-slot="avatar-badge"
    data-position={position}
    className={`${styles.badge} ${className}`.trim()}
    {...props}
  >
    {children}
  </span>
);

/* ── AvatarGroup + AvatarGroupCount ──────────────────────────────────── */

export interface AvatarGroupProps
  extends React.HTMLAttributes<HTMLDivElement> {}

const AvatarGroup = ({ className = '', children, ...props }: AvatarGroupProps) => (
  <div
    data-slot="avatar-group"
    className={`${styles.group} ${className}`.trim()}
    {...props}
  >
    {children}
  </div>
);

export interface AvatarGroupCountProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  count: number;
  size?: AvatarSize;
}

const AvatarGroupCount = ({
  count,
  size = 'default',
  className = '',
  children,
  ...props
}: AvatarGroupCountProps) => (
  <span
    data-slot="avatar-group-count"
    className={`${styles.avatar} ${SIZE_CLASS[size]} ${styles.count} ${className}`.trim()}
    {...props}
  >
    {children ?? `+${count}`}
  </span>
);

export default Avatar;
export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarBadge,
  AvatarGroup,
  AvatarGroupCount,
};
